import React from 'react';
import { Link as RRLink } from 'react-router-dom';
import { Alert, Flex, Link, Text, Box, Button } from 'pounceV2';
import { LinkButtonV3 } from 'Components/buttons/LinkButton/LinkButtonV3';
import { SIDESHEETS } from 'Components/utils/Sidesheet';
import useSidesheetV3 from 'Hooks/useSidesheetV3';
import urls from 'Source/urls';
import { useCSVDownloadV3 } from 'Hooks/useCSVDownloadV3';
import { Notification as NotificationType } from 'Generated/schema';
import { useTheme } from '@mui/material';

export interface CSVNotificationSnackbarProps {
  /** Whether the snackbar is visible */
  isOpen?: boolean;

  /** When the snackbar is discardable, this is the callback that gets fired on discard */
  onClose: () => void;

  /** The variant of notification */
  variant?: 'info' | 'error';

  /** Non-Snackbar props */
  customProps: {
    notification: NotificationType;
  };
}

const CSVNotificationSnackbarV3: React.FC<CSVNotificationSnackbarProps> = ({
  isOpen = true,
  onClose,
  variant,
  customProps,
}) => {
  const { showSidesheet } = useSidesheetV3();
  const theme = useTheme();

  const {
    originalQueryId: queryID,
    presignedUrl,
    presignedUrlTtl,
  } = customProps.notification.metadata;

  const isUrlExpired = React.useMemo(() => {
    if (!presignedUrlTtl || variant === 'error') {
      return false;
    }
    const expirationDate = new Date(presignedUrlTtl);
    return new Date() > expirationDate;
  }, [presignedUrlTtl, variant]);

  const { downloadCSVResults } = useCSVDownloadV3({
    queryId: queryID,
    isAsyncDownload: true,
  });

  if (!isOpen) {
    return null;
  }

  const isSuccess = variant !== 'error';

  const regenerateCSV = () => {
    downloadCSVResults();
  };

  return (
    <Box maxWidth={400}>
      <Alert
        variant={isSuccess ? 'success' : 'error'}
        variantBackgroundStyle="solid"
        discardable={true}
        onClose={onClose}
        title={
          isSuccess ? (
            <Flex direction="column" gap={4}>
              <Text>
                CSV from search query{' '}
                <Link
                  as={RRLink}
                  textDecoration="underline"
                  to={urls.investigate.search({ queryId: queryID })}
                >
                  {queryID}
                </Link>{' '}
                is ready for download
              </Text>
              {isUrlExpired && (
                <Text
                  color={theme.palette.componentTokens.buttons.colors.link.destructive.text.resting}
                >
                  The download link has expired. Please regenerate the CSV.
                </Text>
              )}
              <Text>
                Download links are also made available in the{' '}
                <Text
                  textDecoration="underline"
                  cursor="pointer"
                  display="inline"
                  _hover={{
                    color: theme.palette.componentTokens.buttons.colors.link.secondary.text.hover,
                  }}
                  _focus={{
                    color:
                      theme.palette.componentTokens.buttons.colors.link.secondary.text.selected,
                  }}
                  _active={{
                    color:
                      theme.palette.componentTokens.buttons.colors.link.secondary.text.selected,
                  }}
                  onClick={() => showSidesheet({ sidesheet: SIDESHEETS.NOTIFICATION_CENTER })}
                >
                  Notifications Center
                </Text>
              </Text>
            </Flex>
          ) : (
            <>
              {isUrlExpired ? (
                <>
                  Your CSV generation request from search query{' '}
                  <Link
                    as={RRLink}
                    textDecoration="underline"
                    to={urls.investigate.search({ queryId: queryID })}
                  >
                    {queryID}
                  </Link>{' '}
                  has expired
                </>
              ) : (
                <>
                  There was a problem with generating the CSV from search query{' '}
                  <Link
                    as={RRLink}
                    textDecoration="underline"
                    to={urls.investigate.search({ queryId: queryID })}
                  >
                    {queryID}
                  </Link>
                </>
              )}
            </>
          )
        }
        actions={
          <Box mt={2}>
            {isSuccess ? (
              <LinkButtonV3 external to={presignedUrl} size="medium" variant="outline">
                Download CSV
              </LinkButtonV3>
            ) : (
              <Button variant="outline" onClick={regenerateCSV} size="medium">
                {isUrlExpired ? 'Generate New CSV' : 'Retry Generating CSV'}
              </Button>
            )}
          </Box>
        }
      />
    </Box>
  );
};

export default CSVNotificationSnackbarV3;
